import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { CompareProvider } from './contexts/CompareContext';
import { initAnalytics } from './utils/analytics';
import { initSentry } from './utils/sentry';
import { hasAnalyticsConsent, onConsentChange } from './utils/consent';
import { getCurrentTenant } from './config/tenants';
import ErrorBoundary from './components/ErrorBoundary';
import App from './App';
import './index.css';

// GDPR: initialize tracking (Sentry + analytics) ONLY after explicit consent.
// A Do Not Track (DNT) signal always blocks tracking — see Cookie Policy §8.
// Both inits are idempotent, so this is safe to call again when consent arrives.
const initializeTracking = () => {
  initSentry();
  initAnalytics();
};
if (hasAnalyticsConsent()) {
  initializeTracking();
}
// If the visitor consents later via the cookie banner, start tracking in-session.
onConsentChange((granted) => {
  if (granted) {
    initializeTracking();
  }
});

// Inject dynamic CSP meta tag for current tenant's API URL
const tenant = getCurrentTenant();
const existingCsp = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
if (existingCsp) {
  existingCsp.remove();
}
const cspMeta = document.createElement('meta');
cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
cspMeta.setAttribute('content',
  `default-src 'self'; script-src 'self'; style-src 'self' 'unsafe-inline'; img-src 'self' https: data:; connect-src 'self' ${tenant.apiUrl}; frame-ancestors 'none'`
);
document.head.appendChild(cspMeta);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <LanguageProvider>
              <CompareProvider>
                <App />
              </CompareProvider>
            </LanguageProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
